import { FundPerformance } from '../../../fund-performance/dto/FundPerformance';
import {
  VerticalChartBar,
  VerticalChartObject,
} from '../../../shared/component/vertical-charts/vertical-charts.component';
import { Fund } from "../../../fund-performance/dto/Fund";

export class FpOverviewData {
  all: FundPerformanceOverview[] = [];
  funds: FundPerformanceOverview[] = [];
  spvs: FundPerformanceOverview[] = [];
}

export class GroupedFunds {
  all: Fund[] = [];
  funds: Fund[] = [];
  spvs: Fund[] = [];


  constructor(funds?: Fund[]) {
    this.splitIntoGroups(funds);
    this.sortGroups();
  }

  sortGroups(): void {
    this.funds.sort((a, b) => a.order.inOrgOverviewTable - b.order.inOrgOverviewTable);
    this.spvs.sort((a, b) => a.order.inOrgOverviewTable - b.order.inOrgOverviewTable);
    this.all = [...this.funds, ...this.spvs];
  }

  private splitIntoGroups(funds?: Fund[]): void {
    this.all = funds;
    funds?.forEach((f) => (f?.spv ? this.spvs.push(f) : this.funds.push(f)));
  }
}

export class SelectedFundsAmount {
  all = 0;
  funds = 0;
  spvs = 0;
}

export const FpOverviewDataShownGroups: (keyof FpOverviewData)[] = ['funds', 'spvs'];

export const FundPerformanceOverviewSorting = {
  byFundName: (fpo: FundPerformanceOverview): string => fpo.name,
  byVintage: (fpo: FundPerformanceOverview): Date => fpo.initialClosing,
  byTotalCommitment: (fpo: FundPerformanceOverview): number => fpo.selectedFP.totalCommitment,
  byContributionReceived: (fpo: FundPerformanceOverview): number => fpo.selectedFP.totalCommitment,
  byUnfundedCommitment: (fpo: FundPerformanceOverview): number => fpo.selectedFP.totalCommitment - fpo.selectedFP.contribution,
  bySumNavAndUnfundedCommitment: (fpo: FundPerformanceOverview): number => (fpo.selectedFP.totalCommitment - fpo.selectedFP.contribution) + (fpo.beforeCarry.navAndDistribution - fpo.beforeCarry.distribution),

  byNavAndDistribution: (fpo: FundPerformanceOverview): number => fpo.beforeCarry.navAndDistribution,
  byTotalNavInclCarry: (fpo: FundPerformanceOverview): number => fpo.beforeCarry.navAndDistribution - fpo.beforeCarry.distribution,
  byNetGainLoss: (fpo: FundPerformanceOverview): number => fpo.beforeCarry.ngl,
  // A check was added to handle cases where the value may be NaN.
  byTvpi: (fpo: FundPerformanceOverview): number => (fpo.beforeCarry.tvpi ? fpo.beforeCarry.tvpi : 0),
  byDpi: (fpo: FundPerformanceOverview): number => fpo.beforeCarry.dpi,
  byRvpi: (fpo: FundPerformanceOverview): number => fpo.beforeCarry.rvpi,
  byNetIrr: (fpo: FundPerformanceOverview): number => fpo.selectedFP.netIrr,

  byCarryInterestTotal: (fpo: FundPerformanceOverview): number => fpo.selectedFP.carryInterest,
  byCarryInterestRealized: (fpo: FundPerformanceOverview): number => fpo.selectedFP.carryDistribution,
  byCarryInterestUnrealized: (fpo: FundPerformanceOverview): number => fpo.selectedFP.carryInterest - fpo.selectedFP.carryDistribution,

  byNavAndDistributionAfterCarry: (fpo: FundPerformanceOverview): number => fpo.afterCarry.navAndDistribution,
  byTotalNavInclCarryAfterCarry: (fpo: FundPerformanceOverview): number => fpo.afterCarry.navAndDistribution - fpo.afterCarry.distribution,
  byNetGainLossAfterCarry: (fpo: FundPerformanceOverview): number => fpo.afterCarry.ngl,
  // A check was added to handle cases where the value may be NaN.
  byTvpiAfterCarry: (fpo: FundPerformanceOverview): number => (fpo.afterCarry.tvpi ? fpo.afterCarry.tvpi : 0),
  byDpiAfterCarry: (fpo: FundPerformanceOverview): number => fpo.afterCarry.dpi,
  byRvpiAfterCarry: (fpo: FundPerformanceOverview): number => fpo.afterCarry.rvpi,
  byNetIrrAfterCarry: (fpo: FundPerformanceOverview): number => fpo.selectedFP.netIrrAfterCarry,
};


export class ShowOrgOverview {
  loading = true;
  funds = false;
  spvs = false;
  chart = false;
}

export class ShowFundsTable {
  funds = true;
  spvs = true;
}

export interface FundPerformanceOverview {
  name: string;
  fundUuid: string;
  isShown: boolean;
  selectedFP: FundPerformance;
  fps: FundPerformance[];
  periods?: Date[];
  initialClosing: Date;
  isSpv: boolean;
  orderInTable: number;
  beforeCarry: NglTvpiForCarry;
  afterCarry: NglTvpiForCarry;
}

export class NglTvpiForCarry {
  ngl: number;
  navAndDistribution: number;
  distribution: number;
  tvpi: number;
  dpi: number;
  rvpi: number;
}

export class CarryValues {
  total: number;
  realized: number;
  unrealized: number;
}

export interface ParsedFund {
  name: string;
  uuid: string;
  isSpv: boolean;
  orderInOrgOverviewTable: number;
  fps: FundPerformance[];
  initialClosing?: Date;
}

export class OverviewTotal {
  totalCommitment: number;
  contributionReceivedPercent: number;
  contributionReceivedNumber: number;
  beforeCarry = new NglTvpiForCarry();
  afterCarry = new NglTvpiForCarry();
  carriedInterest = new CarryValues();
}

export class GroupedOverviewTotal {
  all = new OverviewTotal();
  funds = new OverviewTotal();
  spvs = new OverviewTotal();
}

export interface FundsAndPerformanceExportExcelWrapper {
  funds: FundPerformanceOverview[];
  totals: OverviewTotal;
}

export type PPTab = 'Fund' | 'Company';


export const generateGraphicData = (funds: FundPerformanceOverview[]): VerticalChartObject => {
  const gainLossArr = funds.map((f) => f.selectedFP).map((fp) =>
    (fp?.distribution ? fp.distribution : 0) +
    (fp?.navBeforeCarry ? fp.navBeforeCarry : 0) -
    (fp?.contribution ? fp.contribution : 0));
  const netLossExist = Boolean(-gainLossArr.find((g) => g < 0));
  const netGainExist = Boolean(gainLossArr.find((g) => g > 0));

  const agenda = [];
  if (netGainExist) {
    agenda.push({ color: '#5CC3BB', title: 'Net gain' });
  }
  if (netLossExist) {
    agenda.push({ color: '#FF8078', title: 'Net loss' });
  }
  agenda.push({ color: '#5E6F78', title: 'Contribution received' });
  agenda.push({ color: '#B2B2B4', title: 'Total commitment' });
  const graphicData = {
    agenda,
    graphics: [],
  };
  funds.forEach((f) => {
    // if there is no selected, aka no fund, ignores this line.
    if (f.selectedFP) {
      const gainLoss = f.selectedFP.distribution + f.selectedFP.navBeforeCarry - f.selectedFP.contribution;
      const bars: VerticalChartBar[] = [
        {
          parts: [
            {
              color: gainLoss < 0 ? '#FF8078' : '#5CC3BB', amount: gainLoss, pcntAmount: undefined,
              legendHeading: gainLoss < 0 ? 'Net loss' : 'Net gain',
            },
            {
              color: '#5E6F78',
              amount: f.selectedFP.contribution,
              pcntAmount: undefined,
              legendHeading: 'Contribution received',
            },
          ],
        },
        {
          parts: [{
            color: '#B2B2B4',
            amount: f.selectedFP.totalCommitment,
            pcntAmount: undefined,
            legendHeading: 'Total commitment',
          }],
        },
      ];
      graphicData.graphics.push({
        title: f.name,
        bars,
      });
    }
  });
  return graphicData;
};
